import da from "./locales/da.json";
import de from "./locales/de.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import hr from "./locales/hr.json";
import hu from "./locales/hu.json";
import it from "./locales/it.json";
import nl from "./locales/nl.json";
import no from "./locales/no.json";
import pl from "./locales/pl.json";
import pt from "./locales/pt.json";
import ro from "./locales/ro.json";
import sl from "./locales/sl.json";

// A locale message tree: nested string dictionaries (the shape of the locale JSON files).
// Typed this way (rather than `Record<string, unknown>`) so it satisfies vue-i18n's
// LocaleMessageDictionary and `messages` option without pulling in a vue-i18n dependency here.
export type LocaleMessages = { [key: string]: string | LocaleMessages };

// Per-app override sets (layered over sharedMessages via mergeWithShared in the
// app). Kept here so the apps carry no local locale files.
export { downloadOverrides } from "./downloadOverrides";
export { questionnaireOverrides } from "./questionnaireOverrides";

/**
 * Shared message base for the cross-cutting UI chrome (footer, status, themes, ...).
 * These keys are owned here so every persolog app renders them identically.
 * Apps merge their own app-specific keys on top (see `mergeWithShared`).
 */
export const sharedMessages: Record<string, LocaleMessages> = {
    da,
    de,
    en,
    es,
    fr,
    hr,
    hu,
    it,
    nl,
    no,
    pl,
    pt,
    ro,
    sl,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Deep-merge `override` onto `base`; `override` wins on leaf conflicts. */
export function deepMerge(base: Record<string, unknown>, override: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = { ...base };
    for (const [key, value] of Object.entries(override)) {
        const existing = result[key];
        result[key] = isPlainObject(value) && isPlainObject(existing) ? deepMerge(existing, value) : value;
    }
    return result;
}

/**
 * Layer the shared message base under each app-local locale, app keys winning.
 * Only the locales present in `appMessages` are returned, so an app that ships
 * fewer locales (e.g. only de/en) does not inherit the full shared locale set.
 */
export function mergeWithShared(
    appMessages: Record<string, Record<string, unknown>>,
): Record<string, LocaleMessages> {
    const merged: Record<string, LocaleMessages> = {};
    for (const [locale, messages] of Object.entries(appMessages)) {
        merged[locale] = deepMerge(sharedMessages[locale] ?? {}, messages) as LocaleMessages;
    }
    return merged;
}
